import type { ReactNode } from "react";
import { Group, Panel, Separator, useDefaultLayout } from "react-resizable-panels";
import { arrangement, arrangementKey, defaultSizes, type Column } from "../lib/layoutModel.ts";
import type { IdeLayout } from "../lib/useIdeLayout.ts";
import type { IdeHosts } from "./IdeLayout.tsx";
import { HostSlot } from "./PanelHost.tsx";

const storage = (() => {
  try {
    return localStorage;
  } catch {
    return undefined;
  }
})();

const PANEL_CLASS: Record<Column, string> = {
  files: "panel panel-explorer",
  history: "panel panel-history",
  agent: "panel panel-agent",
  center: "panel",
};
const MIN: Record<Column, number> = { files: 170, history: 200, agent: 280, center: 280 };
const MAX: Record<Column, string> = { files: "45%", history: "50%", agent: "60%", center: "100%" };

/**
 * Desktop layout: ONE horizontal group whose columns are exactly the visible panels, in the order given by the layout
 * model (Files / History / Agent on the left or right of the central column). The central column holds Code and Tools
 * independently: both (vertical split), either one alone, or - when neither is visible - no central column at all, so
 * Files, History and Agent share the whole width. Hidden panels are not in the group; their content stays mounted in
 * its host element (see PanelHost), so nothing is lost.
 */
export function WideIde({ hosts, layout }: { hosts: IdeHosts; layout: IdeLayout }) {
  const { model, generation } = layout;
  const a = arrangement(model);
  const key = `${arrangementKey(model)}-${generation}`;
  const groupId = `racler.layout.v3:h:${arrangementKey(model)}`;
  const horizontal = useDefaultLayout({ id: groupId, ...(storage ? { storage } : {}) });
  const sizes = defaultSizes(a);

  const centerNode = <CenterColumn key="center" center={a.center} hosts={hosts} size={sizes["center"]!} />;
  const slot = (c: Exclude<Column, "center">): ReactNode => (
    <Panel
      key={c}
      id={c}
      defaultSize={`${sizes[c]}%`}
      minSize={MIN[c]}
      maxSize={MAX[c]}
      className={PANEL_CLASS[c]}
    >
      <HostSlot host={hosts[c]} />
    </Panel>
  );

  const sequence: ReactNode[] = [];
  a.columns.forEach((c, i) => {
    if (i > 0) sequence.push(<Separator key={`sep-${i}`} className="sep sep-v" />);
    sequence.push(c === "center" ? centerNode : slot(c));
  });

  return (
    <div className="ide" data-testid="ide" data-arrangement={key} data-center={a.center}>
      {a.columns.length === 0 ? (
        <div className="ide-empty muted" data-testid="ide-empty">
          All panels are hidden. Use the panel buttons in the header to bring one back.
        </div>
      ) : (
        <Group
          key={key}
          id={groupId}
          orientation="horizontal"
          defaultLayout={horizontal.defaultLayout}
          onLayoutChanged={horizontal.onLayoutChanged}
          className="ide-group"
        >
          {sequence}
        </Group>
      )}
    </div>
  );
}

function CenterColumn({
  center,
  hosts,
  size,
}: {
  center: "both" | "code" | "tools" | "none";
  hosts: IdeHosts;
  size: number;
}) {
  const vertical = useDefaultLayout({ id: "racler.layout.v3:v", ...(storage ? { storage } : {}) });
  return (
    <Panel id="center" defaultSize={`${size}%`} minSize={MIN.center} className="panel">
      {center === "both" ? (
        <Group
          id="racler.layout.v3:v"
          orientation="vertical"
          defaultLayout={vertical.defaultLayout}
          onLayoutChanged={vertical.onLayoutChanged}
          className="ide-group"
        >
          <Panel id="code" defaultSize="70%" minSize="15%" className="panel panel-editor">
            <HostSlot host={hosts.code} />
          </Panel>
          <Separator className="sep sep-h" />
          <Panel id="tools" defaultSize="30%" minSize={110} maxSize="85%" className="panel">
            <HostSlot host={hosts.tools} />
          </Panel>
        </Group>
      ) : (
        <div className={center === "code" ? "panel panel-editor" : "panel"} style={{ height: "100%" }}>
          <HostSlot host={center === "code" ? hosts.code : hosts.tools} />
        </div>
      )}
    </Panel>
  );
}
